import { parse, format as formatEnglish, isBefore, isAfter, sub } from 'date-fns'
import { format, formatRelative } from '@fs/zion-locale/date-fns'
import { isNullOrUndefinedOrEmpty } from './Utils'

// Convert dates

export const TIME_FORMAT = ' HH:mm:ss'
export const MILLISECONDS_FORMAT = '.SSS'
export const ORIGINAL_DATE_FORMAT = 'DD MMM YYYY'
export const FORMAL_DATE_FORMAT = '+YYYY-MM-DD'
export const GENEALOGICAL_DATE_FORMAT = 'DD MMMM YYYY'
export const NUMERIC_DATE_FORMAT = 'YYYY-MM-DD'
export const YEAR_MONTH_DATE_TIME_FORMAT = 'YYYY MMMM DD, HH:mm:ss.SSS'

export const STANDARDS_DAY_MONTH_YEAR_FORMAT = 'DD MMMM YYYY'
export const STANDARDS_MONTH_YEAR_FORMAT = 'MMMM YYYY'
export const STANDARDS_YEAR_FORMAT = 'YYYY'

// These constants (and any format string passed in by callers) are written in moment's token
// dialect. date-fns matches moment for M/H/h/m/s/S, but spells year and day-of-month
// differently (moment's Y.../D... are date-fns's y.../d... - date-fns's own uppercase Y/D mean
// ISO week-numbering year and day-of-year, which is not what any format here means), so those two
// token families get translated before every parse/format call rather than rewriting every format
// string at every call site.
function toDateFnsFormat(momentFormat) {
  return momentFormat.replace(/Y+/g, (run) => 'y'.repeat(run.length)).replace(/D+/g, (run) => 'd'.repeat(run.length))
}

// date-fns fills any date field missing from the format string using this reference - but only
// ever for fields coarser than the ones present (parsing just a year still zeroes the month/day
// rather than inheriting them), which is what every format string here relies on, so the
// reference's own date is never actually read.
const PARSE_REFERENCE_DATE = new Date(1970, 0, 1)

function parseWithMomentFormat(dateString, momentFormat) {
  const translated = toDateFnsFormat(momentFormat)
  if (translated === 'yy') {
    // moment's lowercase yy (unlike its uppercase YY) parses the literal 2-digit value - "90" is
    // year 90, not 1990, which matters for genealogical records. date-fns's yy instead pivots off
    // a reference century, so this one token is handled directly rather than via that pivot.
    // setFullYear (unlike `new Date(year, ...)`) has no two-digit-year special case of its own.
    const year = Number(dateString)
    const result = new Date(0)
    result.setFullYear(year, 0, 1)
    return Number.isNaN(year) ? new Date(Number.NaN) : result
  }
  return parse(dateString, translated, PARSE_REFERENCE_DATE)
}

// date-fns has no UTC parsing mode - it always builds the result in local time. moment.utc(str, fmt)
// instead reads the string's wall-clock digits and anchors them to UTC, so re-anchor by pulling
// those same digits back out of the local-time parse and rebuilding the instant with Date.UTC.
function parseAsUTC(dateString, dateFnsFormat) {
  const local = parse(dateString, dateFnsFormat, PARSE_REFERENCE_DATE)
  if (Number.isNaN(local.getTime())) {
    return local
  }
  return new Date(
    Date.UTC(
      local.getFullYear(),
      local.getMonth(),
      local.getDate(),
      local.getHours(),
      local.getMinutes(),
      local.getSeconds()
    )
  )
}

// moment's lowercase d-family is day-of-WEEK (dddd/ddd/dd/d = full/abbreviated/2-letter/numeric),
// a completely different field from the uppercase D-family (day-of-month) toDateFnsFormat already
// handles - easy to miss since both look like "day" tokens. dddd/ddd map straight onto date-fns's
// EEEE/EEE; dd (moment's 2-letter abbreviation, e.g. "Th") has no date-fns equivalent at all, so
// it's swapped for a placeholder here and resolved after formatting, once the actual weekday name
// is known. Must run before toDateFnsFormat, on the untouched original string - after that step
// every day-of-month D-run has already been lowercased too, making it indistinguishable from a
// genuine weekday token.
const WEEKDAY_2_LETTER_PLACEHOLDER = '\u0000dd\u0000'
function translateWeekdayTokens(momentFormat) {
  // a lone lowercase 'd' (moment's numeric day-of-week) has no caller in this codebase, so it's
  // intentionally left untranslated rather than guessed at
  return momentFormat
    .replaceAll(/d{4,}/g, 'EEEE')
    .replaceAll(/d{3}/g, 'EEE')
    .replaceAll(/d{2}/g, WEEKDAY_2_LETTER_PLACEHOLDER)
}

// Deliberately not the locale-aware `format` above - these render machine/standards formats
// (e.g. "18 Dec 2020"), which stay in English regardless of the app's current locale, matching
// moment's default (English) locale that this code never changed.
function formatWithMomentFormat(date, momentFormat) {
  // moment().format() on an invalid moment returns the literal string "Invalid date" rather than
  // throwing - some callers (e.g. DmcDatePickerController) branch on that exact string to show a
  // validation error, so this has to match rather than let date-fns's format() throw.
  if (Number.isNaN(date.getTime())) {
    return 'Invalid date'
  }
  const translated = toDateFnsFormat(translateWeekdayTokens(momentFormat))
  if (!translated.includes(WEEKDAY_2_LETTER_PLACEHOLDER)) {
    return formatEnglish(date, translated)
  }
  const threeLetterWeekday = formatEnglish(date, 'EEE')
  return formatEnglish(date, translated.replace(WEEKDAY_2_LETTER_PLACEHOLDER, 'EEE')).replace(
    threeLetterWeekday,
    threeLetterWeekday.slice(0, 2)
  )
}

export function toTimestamp(date) {
  return date.getTime()
}

export function getFormatString(baseString, withTime, withMilliseconds) {
  return `${baseString}${withTime ? TIME_FORMAT : ''}${withMilliseconds ? MILLISECONDS_FORMAT : ''}`
}

export function convertDateString(inputString, inputFormat, outputFormat) {
  if (isNullOrUndefinedOrEmpty(inputString)) {
    return ''
  }
  // moment(aDate, anything) ignores the format and wraps the Date as-is - only string input is
  // ever actually parsed against inputFormat.
  if (inputString instanceof Date) {
    return formatWithMomentFormat(inputString, outputFormat)
  }
  // Special cases that moment does not handle
  if (inputFormat === 'yyyyMMDD') {
    inputString = `${inputString.substring(0, 4)}-${inputString.substring(4, 6)}-${inputString.substring(6)}`
    inputFormat = 'yyyy-MM-DD'
  }

  const timestamp = toTimestamp(parseWithMomentFormat(inputString, inputFormat))
  const outputString = formatWithMomentFormat(new Date(timestamp), outputFormat)
  return outputString
}

// GEDCOM-X-style partial dates (year, year-month, year-month-day, dash-separated) are the only
// real caller of this: moment's non-strict parser accepted "+2010" or "+2010-05" directly against
// the full "+YYYY-MM-DD" format, silently defaulting whatever trailing segment(s) were missing.
// A format without dashes (e.g. ORIGINAL_DATE_FORMAT) has nothing to drop, so this is a no-op for it.
function dateFormatTruncations(dateFormat) {
  const segments = dateFormat.split('-')
  return segments.map((_, index) => segments.slice(0, index + 1).join('-')).reverse()
}

// moment parsed non-strictly, so a shorter input (missing a trailing date segment, or missing
// time, or time but no milliseconds) against this fully-qualified format still succeeded,
// defaulting whatever the string didn't provide. date-fns requires the input to structurally
// match the whole format, so try every (date length × time length) combination, most-specific
// first, and fall back until one actually parses.
export function dateStringToTimestamp(str, dateFormat) {
  const timeSuffixes = [`${TIME_FORMAT}${MILLISECONDS_FORMAT}`, TIME_FORMAT, '']
  const candidateFormats = dateFormatTruncations(dateFormat).flatMap((datePart) =>
    timeSuffixes.map((timeSuffix) => `${datePart}${timeSuffix}`)
  )
  const parsedAttempts = candidateFormats.map((candidateFormat) => parseWithMomentFormat(str, candidateFormat))
  const parsed = parsedAttempts.find((attempt) => !Number.isNaN(attempt.getTime())) || parsedAttempts[0]
  return toTimestamp(parsed)
}

export function timestampToDateString({
  timestamp,
  withTime,
  withMilliseconds,
  dateFormat = GENEALOGICAL_DATE_FORMAT,
  timestampIsSeconds,
}) {
  // Handles unexpected differences in Jackson 3 deserialization
  if (typeof timestamp === 'string') {
    if (timestamp.includes('T') && timestamp.includes('Z')) {
      return convertUTCString(timestamp, true)
    }
    return timestamp
  }
  const formatString = getFormatString(dateFormat, withTime, withMilliseconds)
  return formatWithMomentFormat(new Date(timestampIsSeconds ? timestamp * 1000 : timestamp), formatString)
}

export function UTCDateStringToTimestamp(utcString) {
  // Takes 2020-12-18T22:05:38.189Z, Fri Feb 13 22:12:23 UTC 2026, or +2023-08-26 (GEDCOM-X date)
  let timestamp

  // Check if it's in Unix date format (e.g., "Fri Feb 13 22:12:23 UTC 2026")
  // Pattern: starts with day name, contains UTC but not T (ISO format has T)
  if (utcString && typeof utcString === 'string' && /^[A-Z][a-z]{2}\s/.test(utcString) && utcString.includes(' UTC ')) {
    // Parse Unix date format: ddd MMM DD HH:mm:ss [UTC] YYYY
    // The 'UTC' literal in the pattern matches the fixed word in the string, not a UTC parse mode
    timestamp = toTimestamp(parseAsUTC(utcString, "EEE MMM dd HH:mm:ss 'UTC' yyyy"))
  } else if (utcString && typeof utcString === 'string' && utcString.startsWith('+')) {
    // Parse GEDCOM-X formal date format e.g. +2023-08-26
    timestamp = toTimestamp(parseAsUTC(utcString.slice(1), 'yyyy-MM-dd'))
  } else {
    // Parse ISO format or other standard formats
    timestamp = toTimestamp(new Date(utcString))
  }

  return timestamp
}

export function convertUTCString(utcString, withTime, withMilliseconds, dateFormat = GENEALOGICAL_DATE_FORMAT) {
  const timestamp = UTCDateStringToTimestamp(utcString)
  return timestampToDateString({ timestamp, withTime, withMilliseconds, dateFormat })
}

export function toLocaleString(date, includeTime = false) {
  let result = ''
  try {
    if (date) {
      if (includeTime) {
        result = format(date, 'd MMMM yyyy p')
      } else {
        result = format(date, 'd MMMM yyyy')
      }
    }
  } catch (e) {
    result = ''
  }
  return result
}

export function utcToLocaleString(UTCStr, includeTime = false) {
  let localeDate = ''
  try {
    if (UTCStr) {
      // moment.utc(aDate, [fmt, ...], true) ignores the format and wraps the Date as-is - only
      // string input is ever actually parsed.
      let date = UTCStr instanceof Date ? UTCStr : parseAsUTC(UTCStr, 'dd MMMM yyyy HH:mm')
      // moment.utc(str, [fmt, moment.ISO_8601], true) tried the explicit format first, then fell
      // back to ISO_8601 - date-fns has no multi-format/fallback parse, so try each in turn.
      if (Number.isNaN(date.getTime())) {
        date = new Date(UTCStr)
      }
      if (Number.isNaN(date.getTime())) {
        throw new TypeError(`Unparseable date: ${UTCStr}`)
      }
      localeDate = toLocaleString(date, includeTime)
    }
  } catch (e) {
    // Passthrough matches moment's behavior for strings that couldn't be reformatted, but a
    // non-string input (e.g. an Invalid Date) must not leak out — React crashes rendering it
    // with "Objects are not valid as a React child (found: Invalid Date)".
    localeDate = typeof UTCStr === 'string' ? UTCStr : ''
  }
  return localeDate
}

export function toLocaleRelativeString(date, dateToCompare) {
  try {
    return formatRelative(date, dateToCompare)
  } catch {
    return ''
  }
}

export function arrayToLocalDateTimeString(dateArray) {
  try {
    const [year, month, day, hour, minute, second] = dateArray
    const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second))
    return toLocaleString(date, true)
  } catch (e) {
    return ''
  }
}

/**
 * The browser's local time zone abbreviation (MST, MDT, GMT+2, ...).
 *
 * @param {Date} [date] the instant to read the abbreviation for; defaults to now. Pass the instant
 *   actually being displayed whenever there is one — a zone's abbreviation changes across a DST
 *   boundary, so reading it off "now" labels a summer timestamp MST when viewed in winter.
 * @returns {string}
 */
export function getLocalTimeZoneAbbreviation(date = new Date()) {
  return new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
    .formatToParts(date)
    .find((part) => part.type === 'timeZoneName').value
}

function toCompareDate(dateOrOffset, dateFormat) {
  if (dateOrOffset instanceof Date || typeof dateOrOffset === 'number') {
    return new Date(dateOrOffset)
  }
  if (typeof dateOrOffset === 'string') {
    return parseWithMomentFormat(dateOrOffset, dateFormat)
  }
  // Plain object treated as a duration offset from now: { months: 13 }, { days: 1 }, etc.
  return sub(new Date(), dateOrOffset)
}

/**
 * Returns true if `dateStr` represents a calendar day strictly before the comparison target.
 * Returns false if `dateStr` is null, undefined, or empty.
 * Comparison is day-granular — time of day is ignored.
 *
 * The second argument accepts several forms:
 *   - A date string in the same format as `dateFormat` (default YYYY-MM-DD)
 *       isDateBefore('2025-01-01', '2025-06-01')         // true
 *   - A Date object or numeric timestamp
 *       isDateBefore('2020-01-01', new Date())           // true
 *   - A moment duration object — interpreted as an offset subtracted from today
 *       isDateBefore(startDate, { months: 13 })          // true if startDate is more than 13 months ago
 *       isDateBefore(startDate, { years: 2, days: 15 })  // combined offsets are supported
 *
 * @param {string} dateStr - The date to test, formatted according to `dateFormat`
 * @param {string|Date|number|Object} dateOrOffset - The date or duration offset to compare against
 * @param {string} [dateFormat=NUMERIC_DATE_FORMAT] - moment format string for parsing `dateStr` (and `dateOrOffset` when it is a string)
 * @returns {boolean}
 */
export function isDateBefore(dateStr, dateOrOffset, dateFormat = NUMERIC_DATE_FORMAT) {
  if (isNullOrUndefinedOrEmpty(dateStr)) return false
  return isBefore(
    startOfDay(parseWithMomentFormat(dateStr, dateFormat)),
    startOfDay(toCompareDate(dateOrOffset, dateFormat))
  )
}

/**
 * Returns true if `dateStr` represents a calendar day strictly after the comparison target.
 * Returns false if `dateStr` is null, undefined, or empty.
 * Comparison is day-granular — time of day is ignored.
 *
 * The second argument accepts several forms:
 *   - A date string in the same format as `dateFormat` (default YYYY-MM-DD)
 *       isDateAfter('2025-06-01', '2025-01-01')          // true
 *   - A Date object or numeric timestamp
 *       isDateAfter('2099-01-01', new Date())            // true
 *   - A moment duration object — interpreted as an offset subtracted from today
 *       isDateAfter(endDate, { days: 1 })                // true if endDate is today or in the future
 *       isDateAfter(endDate, { years: 1, months: 6 })    // combined offsets are supported
 *
 * @param {string} dateStr - The date to test, formatted according to `dateFormat`
 * @param {string|Date|number|Object} dateOrOffset - The date or duration offset to compare against
 * @param {string} [dateFormat=NUMERIC_DATE_FORMAT] - moment format string for parsing `dateStr` (and `dateOrOffset` when it is a string)
 * @returns {boolean}
 */
export function isDateAfter(dateStr, dateOrOffset, dateFormat = NUMERIC_DATE_FORMAT) {
  if (isNullOrUndefinedOrEmpty(dateStr)) return false
  return isAfter(
    startOfDay(parseWithMomentFormat(dateStr, dateFormat)),
    startOfDay(toCompareDate(dateOrOffset, dateFormat))
  )
}

function startOfDay(date) {
  const result = new Date(date)
  result.setHours(0, 0, 0, 0)
  return result
}
